import { Injectable } from '@angular/core';
import {
  IPersonalDetails,
  IInsuranceHistory,
  IProviders,
  IUser,
  IAccountDetails,
  SharedConfig,
  IUserAllRelatedData,
  IUserPermission,
  IEmailAuth,
  ReqResponse,
  IUserResetPassword,
  IAddresses,
  IGeneralCodeDescriptionType,
} from '@takectrl-multiapp/svcs-share-config';
import { Observable, of, timer } from 'rxjs';
import { APIHttpService } from './api-http.service';
import { ICtrlFileResponse, ICtrlResponse } from '../interfaces/api.interface';
import { finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ApiCacheService } from './api-cache.service';
import { HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class APIUserService {
  public readonly CACHE_KEYS = {
    USER_DATA: 'all_related_data',
    USER_PERMISSIONS: 'permissions',
    USER_DOCUMENTS: 'documents',
    USER: 'details',
    USER_BY_EMAIL: 'email_lookup',
    GLOBAL_CONFIG: 'global_config',
  };
  
  private userRequestInProgress: Observable<ICtrlResponse<IUserAllRelatedData>> | null = null;
  
  private readonly delayBetweenRequests = 1000;

  constructor(
    private readonly httpService: APIHttpService,
    private readonly sharedConfig: SharedConfig,
    private readonly cacheService: ApiCacheService
  ) {}

  checkEmailForSpam(email: string): Observable<IEmailAuth> {
    return this.httpService.post('/users/verify/email/', { email });
  }

  provinces(): Observable<ICtrlResponse<IGeneralCodeDescriptionType>> {
    const headers = new HttpHeaders({
      'x-channel-key': 'CTRLCLIENT',
    });
    return this.httpService.get(`/providers/premia/values/address_province/`, {}, headers)
  }

  estates(search: string): Observable<ICtrlResponse<IGeneralCodeDescriptionType>> {
    const headers = new HttpHeaders({
      'x-channel-key': 'CTRLCLIENT',
    });
    return this.httpService.get(`/providers/premia/values/address_estate/?search=${search}`, {}, headers)
  }

  sectionalTitles(search: string): Observable<ICtrlResponse<IGeneralCodeDescriptionType>> {
    const headers = new HttpHeaders({
      'x-channel-key': 'CTRLCLIENT',
    });
    return this.httpService.get(`/providers/premia/values/address_sectional/?search=${search}`, {}, headers)
  }

  street(suburb_code: string): Observable<ICtrlResponse<IGeneralCodeDescriptionType>> {
    const headers = new HttpHeaders({
      'x-channel-key': 'CTRLCLIENT',
    });
    return this.httpService.get(`/providers/premia/values/address_street/?suburb_code=${suburb_code}`, {}, headers)
  }

  suburb(province_code: string): Observable<ICtrlResponse<IGeneralCodeDescriptionType>> {
    const headers = new HttpHeaders({
      'x-channel-key': 'CTRLCLIENT',
    });
    return this.httpService.get(`/providers/premia/values/address_suburb/?province_code=${province_code}`, {}, headers)
  }

  getUserPermissions(): Observable<ICtrlResponse<IUserPermission>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.USER_PERMISSIONS);
    if (cached) return of(cached);

    return this.httpService
      .get(`/users/get_user_permissions/`, {})
      .pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.USER_PERMISSIONS, response)));
  }

  checkIfUserMsisdnIsRegistered(phoneNumber: string): Observable<ReqResponse> {
    return this.httpService.get(
      `${SharedConfig.appCredentials.firebase_functions_url}/authentication/user-record-by-msisdn?msisdn=${phoneNumber.substring(1)}`
    );
  }

  getUser(firebase_uuid: string): Observable<ICtrlResponse<IUser>> {
    return this.httpService.get(`/users/`, { firebase_uuid })
  }

  createLead(): Observable<ICtrlResponse<unknown>> {
    const headers = new HttpHeaders({
      'x-channel-key': 'CTRLCLIENT',
    });
    return this.httpService.post(`/providers/premia/create_lead/`, {}, headers)
  }

  // setUserLocalCache(firebase_uuid: string): Observable<ICtrlResponse<IUser>> {
  //   const cached = this.cacheService.get(`${this.CACHE_KEYS.USER}_${firebase_uuid}`);
  //   if (cached) return of(cached);

  //   return this.httpService
  //     .get(`/users/`, { firebase_uuid })
  //     .pipe(tap((response) => this.cacheService.set(`${this.CACHE_KEYS.USER}_${firebase_uuid}`, response)));
  // }

  // getUserByUserId(user_id: number): Observable<ICtrlResponse<IUser>> {
  //   const cached = this.cacheService.get(`${this.CACHE_KEYS.USER}_${user_id}`);
  //   if (cached) return of(cached);

  //   return this.httpService
  //     .get(`/users/`, { user_id })
  //     .pipe(tap((response) => this.cacheService.set(`${this.CACHE_KEYS.USER}_${user_id}`, response)));
  // }
  getUserByEmail(email: string): Observable<ICtrlResponse<IUser>> {
    const cached = this.cacheService.get(`${this.CACHE_KEYS.USER_BY_EMAIL}_${email}`);
    if (cached) return of(cached);

    return this.httpService
      .post(`/admin/user/lookup/`, { email })
      .pipe(tap((response) => this.cacheService.set(`${this.CACHE_KEYS.USER_BY_EMAIL}_${email}`, response)));
  }

  createUser(userDetails: IUser): Observable<ICtrlResponse<IUser>> {
    const payload = {
      ...userDetails,
      firebase_uuid: SharedConfig.firebaseUserKey,
    };

    return this.httpService.post(`/users/`, payload).pipe(
      tap(() => {
        this.cacheService.clear(this.CACHE_KEYS.USER_DATA);
        this.cacheService.clear(`${this.CACHE_KEYS.USER}_${userDetails.id}`);
      })
    );
  }

  updateUser(userDetails: IUser): Observable<ICtrlResponse<IUser>> {
    return this.httpService.patch(`/users/${this.sharedConfig.user.id}/`, userDetails).pipe(
      tap(() => {
        // Clear only user-related cache
        this.cacheService.clear(this.CACHE_KEYS.USER_DATA);
        this.cacheService.clear(`${this.CACHE_KEYS.USER}_${userDetails.id}`);
      })
    );
  }

  updatePersonalDetails(personal_details: IPersonalDetails): Observable<void> {
    return this.httpService.patch(`/users/personal-details/${this.sharedConfig.user.personal_details.id}/`, personal_details).pipe(
      map(() => void 0),
      tap(() => {
        // Only clear user data as personal details are part of it
        this.cacheService.clear(this.CACHE_KEYS.USER_DATA);
      })
    );
  }

  updateProviders(providers: IProviders): Observable<void> {
    return this.httpService.post(`/users/providers/${this.sharedConfig.user.id}/`, providers).pipe(
      map(() => void 0),
      tap(() => {
        // Only clear user data as providers are part of it
        this.cacheService.clear(this.CACHE_KEYS.USER_DATA);
      })
    );
  }

  updateUserAccountDetails(account_details: IAccountDetails): Observable<void> {
    return this.httpService.patch(`/users/account-details/${this.sharedConfig.user.account_details.id}/`, account_details).pipe(
      map(() => void 0),
      tap(() => {
        // Only clear user data as account details are part of it
        this.cacheService.clear(this.CACHE_KEYS.USER_DATA);
      })
    );
  }

  createUserAccountDetails(account_details: IAccountDetails): Observable<void> {
    return this.httpService.post(`/users/account-details/${this.sharedConfig.user.account_details.id}/`, account_details).pipe(
      map(() => void 0),
      tap(() => {
        this.cacheService.clear(this.CACHE_KEYS.USER_DATA);
      })
    );
  }

  createUserInsuranceHistory(insuranceHistory: IInsuranceHistory): Observable<ICtrlResponse<IInsuranceHistory>> {
    return this.httpService.post(`/users/insurance-history/`, insuranceHistory).pipe(
      map((response) => {return response}),
      tap(() => {
        this.cacheService.clear(this.CACHE_KEYS.USER_DATA);
      })
    );
  }

  updateUserInsuranceHistory(insuranceHistory: IInsuranceHistory): Observable<void> {
    if (!insuranceHistory) return of(void 0);
    
    return this.httpService.patch(`/users/insurance-history/${insuranceHistory.id}/`, insuranceHistory).pipe(
      map(() => void 0),
      tap(() => {
        this.cacheService.clear(this.CACHE_KEYS.USER_DATA);
      })
    );
  }

  getDocuments(): Observable<ICtrlResponse<ICtrlFileResponse[]>> {
    const cached = this.cacheService.get(this.CACHE_KEYS.USER_DOCUMENTS);
    if (cached) return of(cached);

    return this.httpService
      .get(`/files/list/${this.sharedConfig.user.id}/user/`)
      .pipe(tap((response) => this.cacheService.set(this.CACHE_KEYS.USER_DOCUMENTS, response)));
  }

  updateUserEmail(email: string): Observable<ICtrlResponse<unknown>> {
    return this.httpService.post(`/users/update/email/`, { email }).pipe(
      tap(() => {
        this.cacheService.clear(this.CACHE_KEYS.USER_DATA);
        this.cacheService.clear(`${this.CACHE_KEYS.USER_BY_EMAIL}_${email}`);
      })
    );
  }

  addReferral(name: string, surname: string, email: string): Observable<ICtrlResponse<unknown>> {
    const referral = { preferred_name: name, surname, email };
    return this.httpService.post(`/user_referrals/`, referral);
  }

  deleteUserAccount(): Observable<any> {
    return this.httpService.delete(`/users/`).pipe(tap(() => this.cacheService.clear()));
  }

  handleUserSessionChange(newUserId?: string): void {
    this.cacheService.clear();

    if (newUserId) {
      this.getUserPermissions().subscribe();
    }
  }

  getUserAddresses(): Observable<ICtrlResponse<Array<IAddresses>>> {
    return this.httpService.get(`/addresses/`);
  }

  getAddressBasedOnID(addressId: any): Observable<ICtrlResponse<IAddresses>> {
    return this.httpService.get(`/addresses/${addressId}/`);
  }

  resetPassword(userEmail: string): Observable<ICtrlResponse<IUserResetPassword>> {
    return this.httpService.post(`/users/update/password/`, {email: userEmail});
  }
}
